import styled from '@emotion/styled'
import { asField, FormValue, FieldContext } from 'informed'
import React, { useCallback, HTMLAttributes, RefObject } from 'react'

import { overrideOptional } from '@emico/overrides'

import ButtonPrimary from './ButtonPrimary'

interface Props extends HTMLAttributes<HTMLInputElement> {
  initialValue?: string
  forwardedRef?: RefObject<HTMLInputElement>
  disabled?: boolean
  min?: number
  max?: number
  analyticsContext: string
}

const SpinnerWrapper = styled.div`
  display: inline-flex;
  justify-content: stretch;
`

const SpinnerInput = styled.input`
  width: 4em;
  text-align: center;
`

export const MinButton = overrideOptional(ButtonPrimary)
export const PlusButton = overrideOptional(ButtonPrimary)

const Spinner = asField<Props, FormValue<number>>(
  ({
    fieldState,
    fieldApi,
    onChange,
    onBlur,
    initialValue,
    forwardedRef,
    min = Number.MIN_SAFE_INTEGER,
    max = Number.MAX_SAFE_INTEGER,
    disabled,
    analyticsContext,
    className,
    ...props
  }: FieldContext<FormValue<number>> & Props) => {
    const { value } = fieldState
    const { setValue, setTouched } = fieldApi

    const setSafeValue = useCallback(
      (value) => {
        // If the current value is not a number, just ignore it. Validation will
        // warn the user that it is not valid input.
        if (isNaN(value)) {
          return
        }

        const clamped = Math.max(min, Math.min(max, value))

        // Store as string to keep the types the same
        setValue(String(clamped))
      },
      [max, min, setValue],
    )

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSafeValue(e.currentTarget.value)
        onChange?.(e)
      },
      [onChange, setSafeValue],
    )
    const handleBlur = useCallback(
      (e) => {
        setTouched(true)
        onBlur?.(e)
      },
      [onBlur, setTouched],
    )
    const handleMin = useCallback(() => {
      setSafeValue(Number(value ?? 0) - 1)
    }, [setSafeValue, value])
    const handlePlus = useCallback(() => {
      setSafeValue(Number(value ?? 0) + 1)
    }, [setSafeValue, value])

    return (
      <SpinnerWrapper className={className}>
        <MinButton
          analyticsContext={analyticsContext}
          analyticsName="Decrease quantity"
          onClick={handleMin}
          type="button"
          disabled={disabled || Number(value) <= min}
        >
          -
        </MinButton>
        <SpinnerInput
          pattern={min < 0 ? '-?[0-9]+' : '[0-9]+'}
          inputMode="numeric"
          value={value ? String(value) : ''}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          {...props}
          ref={forwardedRef}
        />
        <PlusButton
          analyticsContext={analyticsContext}
          analyticsName="Increase quantity"
          onClick={handlePlus}
          type="button"
          disabled={disabled || Number(value) >= max}
        >
          +
        </PlusButton>
      </SpinnerWrapper>
    )
  },
)

export default overrideOptional(Spinner)
